import React, { useState, useRef, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import TentativeWspGroups from './TentativeWspGroups';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const ProductFormDialog = ({
  isOpen, setIsOpen,
  productForm, setProductForm,
  handleFormChange, handleSubmit,
  handleImageUpload, handleBulkImageUpload, handleCameraCapture, handleDrop,
  uploading,
  fileInputRef, cameraInputRef,
  enums, brands, vendors,
  newColorInput, setNewColorInput,
  newCategoryInput, setNewCategoryInput,
  newOccasionInput, setNewOccasionInput,
  newFabricInput, setNewFabricInput,
  addCustomEnum,
  resetForm,
}) => {
  const [vendorSearch, setVendorSearch] = useState('');
  const [vendorDropdownOpen, setVendorDropdownOpen] = useState(false);
  const vendorRef = useRef(null);

  // Close vendor dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (vendorRef.current && !vendorRef.current.contains(e.target)) setVendorDropdownOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Reset vendor search when dialog opens/closes
  useEffect(() => {
    if (!isOpen) { setVendorSearch(''); setVendorDropdownOpen(false); }
  }, [isOpen]);

  const selectedVendor = (vendors || []).find(v => v.id === productForm.vendor_id);
  const filteredVendors = (vendors || []).filter(v => {
    if (!vendorSearch.trim()) return true;
    const q = vendorSearch.toLowerCase();
    return v.name?.toLowerCase().includes(q) || v.city?.toLowerCase().includes(q);
  }).slice(0, 15);
  return (
    <Dialog open={isOpen} onOpenChange={(open) => { setIsOpen(open); if (!open) resetForm(); }}>
      <DialogTrigger asChild>
        <Button size="sm" className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-sm" data-testid="add-product-button">
          <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" /></svg>
          Add New Design
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl z-50 max-h-[90vh] overflow-y-auto"
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>Add New Design</DialogTitle>
          <DialogDescription>Register a new design. Sizes, colors &amp; stock will be added during Purchase Entry. Use Cancel or the ✕ to close.</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-6 pt-4">
          {/* Image Upload - Multi Image */}
          <div className="space-y-2">
            <Label>Product Images <span className="text-xs text-gray-400 font-normal">(one per colour)</span></Label>
            {productForm.images.length > 0 && (
              <div className="space-y-2">
                <div className="flex gap-3 flex-wrap">
                  {productForm.images.map((img, idx) => (
                    <div key={idx} className="relative group">
                      <div className="w-20 h-20 rounded-lg overflow-hidden border-2 border-blue-200 shadow-sm">
                        <img src={`${API_BASE}${img}`} alt={`Product ${idx + 1}`} className="w-full h-full object-cover" />
                      </div>
                      <select
                        value={productForm.image_colors?.[img] || ''}
                        onChange={e => setProductForm(prev => ({ ...prev, image_colors: { ...prev.image_colors, [img]: e.target.value } }))}
                        className="w-20 h-6 mt-1 text-[10px] border border-gray-300 rounded px-1 bg-white focus:outline-none focus:ring-1 focus:ring-blue-400"
                        data-testid={`image-color-select-${idx}`}
                      >
                        <option value="">Colour</option>
                        {enums.colors.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                      <button type="button" onClick={() => {
                        setProductForm(prev => {
                          const newColors = { ...prev.image_colors };
                          delete newColors[img];
                          return { ...prev, images: prev.images.filter((_, i) => i !== idx), image_colors: newColors };
                        });
                      }} className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs hover:bg-red-600 opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`remove-image-${idx}`}>
                        x
                      </button>
                    </div>
                  ))}
                </div>
                <div className="flex gap-1 items-center">
                  <Input className="h-7 text-xs flex-1 max-w-[180px]" placeholder="Add new colour..." value={newColorInput} onChange={e => setNewColorInput(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addCustomEnum('colors', newColorInput, setNewColorInput); } }} data-testid="add-new-color-input" />
                  <Button type="button" size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => addCustomEnum('colors', newColorInput, setNewColorInput)} disabled={!newColorInput.trim()} data-testid="add-new-color-btn">+ Add Colour</Button>
                </div>
              </div>
            )}
            <div className="flex items-center gap-3">
              <div className="w-20 h-20 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center bg-gray-50 cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors shrink-0"
                onClick={() => fileInputRef.current?.click()} data-testid="image-upload-area">
                {uploading ? (
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                ) : (
                  <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M12 4v16m8-8H4" />
                  </svg>
                )}
              </div>
              <button type="button" onClick={() => cameraInputRef.current?.click()}
                className="w-20 h-20 rounded-lg border-2 border-dashed border-gray-300 flex flex-col items-center justify-center bg-gray-50 cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors shrink-0"
                data-testid="camera-capture-btn">
                <svg className="w-7 h-7 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                <span className="text-[10px] text-gray-400 mt-0.5">Camera</span>
              </button>
              <div className="text-sm text-gray-500">
                <p className="font-medium">Add image</p>
                <p>JPEG, PNG or WebP · max 2 MB each</p>
              </div>
            </div>
            <div
              className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center cursor-pointer hover:border-blue-400 hover:bg-blue-50/30 transition-colors"
              onDragOver={e => { e.preventDefault(); e.currentTarget.classList.add('border-blue-500', 'bg-blue-50'); }}
              onDragLeave={e => { e.currentTarget.classList.remove('border-blue-500', 'bg-blue-50'); }}
              onDrop={e => { e.currentTarget.classList.remove('border-blue-500', 'bg-blue-50'); handleDrop(e, setProductForm, fileInputRef); }}
              onClick={() => { const el = document.getElementById('bulk-file-input'); if (el) el.click(); }}
              data-testid="bulk-upload-dropzone"
            >
              <svg className="w-6 h-6 mx-auto text-gray-400 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" /></svg>
              <p className="text-xs text-gray-500">Drag & drop multiple images or <span className="text-blue-600 font-medium">browse</span></p>
            </div>
            <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp" multiple onChange={handleImageUpload} className="hidden" data-testid="image-file-input" />
            <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" onChange={e => handleCameraCapture(e, setProductForm)} className="hidden" data-testid="camera-file-input" />
            <input id="bulk-file-input" type="file" accept="image/jpeg,image/png,image/webp" multiple onChange={e => handleBulkImageUpload(e.target.files, setProductForm, { current: e.target })} className="hidden" data-testid="bulk-file-input" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Design Number *</Label>
              <Input name="design_code" value={productForm.design_code} onChange={handleFormChange} placeholder="e.g., 1001" required className="uppercase" data-testid="product-design-code-input" />
            </div>
            <div className="space-y-2 relative" ref={vendorRef}>
              <Label>Vendor *</Label>
              {selectedVendor ? (
                <div className="flex items-center gap-2 h-9 px-3 border rounded-md bg-blue-50 border-blue-200">
                  <span className="text-sm font-medium text-blue-800 flex-1 truncate">{selectedVendor.name}{selectedVendor.city ? ` (${selectedVendor.city})` : ''}</span>
                  <button type="button" onClick={() => { setProductForm(prev => ({ ...prev, vendor_id: '' })); setVendorSearch(''); }} className="text-blue-400 hover:text-red-500 shrink-0" data-testid="clear-vendor-btn">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
                  </button>
                </div>
              ) : (
                <Input
                  value={vendorSearch}
                  onChange={e => { setVendorSearch(e.target.value); setVendorDropdownOpen(true); }}
                  onFocus={() => setVendorDropdownOpen(true)}
                  placeholder="Search vendor..."
                  data-testid="product-vendor-search"
                />
              )}
              {vendorDropdownOpen && !selectedVendor && (
                <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto" data-testid="vendor-dropdown">
                  {filteredVendors.length === 0 && <div className="px-3 py-2 text-sm text-gray-400">No vendors found</div>}
                  {filteredVendors.map(v => (
                    <div key={v.id} className="px-3 py-2 hover:bg-blue-50 cursor-pointer text-sm"
                      onMouseDown={(e) => { e.preventDefault(); setProductForm(prev => ({ ...prev, vendor_id: v.id })); setVendorSearch(''); setVendorDropdownOpen(false); }}
                      data-testid={`vendor-option-${v.id}`}>
                      <span className="font-medium">{v.name}</span>
                      {v.city && <span className="text-gray-400 ml-1">({v.city})</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Category *</Label>
              <div className="flex flex-wrap gap-2 p-3 border rounded-md bg-gray-50/50 min-h-[42px]" data-testid="product-category-pills">
                {enums.categories.map(c => {
                  const isSelected = productForm.category === c;
                  return (
                    <button key={c} type="button" onClick={() =>
                      setProductForm(prev => ({ ...prev, category: isSelected ? '' : c }))
                    } className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm border transition-all ${isSelected ? 'bg-blue-600 text-white border-blue-600 shadow-sm' : 'bg-white text-gray-600 border-gray-300 hover:border-blue-400 hover:bg-blue-50'}`} data-testid={`category-pill-${c}`}>
                      {isSelected && <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" /></svg>}
                      {c}
                    </button>
                  );
                })}
              </div>
              <div className="flex gap-1">
                <Input className="h-7 text-xs flex-1" placeholder="Add new category..." value={newCategoryInput} onChange={e => setNewCategoryInput(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addCustomEnum('categories', newCategoryInput, setNewCategoryInput); } }} data-testid="add-new-category-input" />
                <Button type="button" size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => addCustomEnum('categories', newCategoryInput, setNewCategoryInput)} disabled={!newCategoryInput.trim()} data-testid="add-new-category-btn">+ Add</Button>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Brand *</Label>
              <div className="flex flex-wrap gap-2 p-3 border rounded-md bg-gray-50/50 min-h-[42px]" data-testid="product-brand-pills">
                {[...brands].sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' })).map(b => {
                  const isSelected = productForm.brand_id === b.id;
                  return (
                    <button key={b.id} type="button" onClick={() =>
                      setProductForm(prev => ({ ...prev, brand_id: isSelected ? '' : b.id }))
                    } className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm border transition-all ${isSelected ? 'bg-blue-600 text-white border-blue-600 shadow-sm' : 'bg-white text-gray-600 border-gray-300 hover:border-blue-400 hover:bg-blue-50'}`} data-testid={`brand-pill-${b.id}`}>
                      {isSelected && <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" /></svg>}
                      {b.name}
                    </button>
                  );
                })}
                {brands.length === 0 && <span className="text-xs text-gray-400">No brands yet — add one via Brand Management</span>}
              </div>
            </div>
            <div className="space-y-2">
              <Label>Occasion * <span className="text-xs text-gray-400 font-normal">(select one or more)</span></Label>
              <div className="flex flex-wrap gap-2 p-3 border rounded-md bg-gray-50/50 min-h-[42px]" data-testid="product-theme-select">
                {enums.design_themes.map(t => {
                  const isSelected = productForm.design_theme.includes(t);
                  return (
                    <button key={t} type="button" onClick={() => {
                      setProductForm(prev => ({
                        ...prev,
                        design_theme: isSelected ? prev.design_theme.filter(x => x !== t) : [...prev.design_theme, t]
                      }));
                    }} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm border transition-all ${isSelected ? 'bg-blue-600 text-white border-blue-600 shadow-sm' : 'bg-white text-gray-600 border-gray-300 hover:border-blue-400 hover:bg-blue-50'}`} data-testid={`occasion-${t}`}>
                      {isSelected && <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" /></svg>}
                      {t}
                    </button>
                  );
                })}
              </div>
              <div className="flex gap-1">
                <Input className="h-7 text-xs flex-1" placeholder="Add new occasion..." value={newOccasionInput} onChange={e => setNewOccasionInput(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addCustomEnum('occasions', newOccasionInput, setNewOccasionInput); } }} data-testid="add-new-occasion-input" />
                <Button type="button" size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => addCustomEnum('occasions', newOccasionInput, setNewOccasionInput)} disabled={!newOccasionInput.trim()} data-testid="add-new-occasion-btn">+ Add</Button>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Fabric</Label>
            <div className="flex flex-wrap gap-2 p-3 border rounded-md bg-gray-50/50 min-h-[42px]" data-testid="product-fabric-pills">
              {(enums.fabrics || []).map(f => {
                const isSelected = productForm.fabric === f;
                return (
                  <button key={f} type="button" onClick={() =>
                    setProductForm(prev => ({ ...prev, fabric: isSelected ? '' : f }))
                  } className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm border transition-all ${isSelected ? 'bg-blue-600 text-white border-blue-600 shadow-sm' : 'bg-white text-gray-600 border-gray-300 hover:border-blue-400 hover:bg-blue-50'}`} data-testid={`fabric-pill-${f}`}>
                    {isSelected && <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" /></svg>}
                    {f}
                  </button>
                );
              })}
            </div>
            <div className="flex gap-1">
              <Input className="h-7 text-xs flex-1" placeholder="Add new fabric..." value={newFabricInput} onChange={e => setNewFabricInput(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addCustomEnum('fabrics', newFabricInput, setNewFabricInput); } }} data-testid="add-new-fabric-input" />
              <Button type="button" size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => addCustomEnum('fabrics', newFabricInput, setNewFabricInput)} disabled={!newFabricInput.trim()} data-testid="add-new-fabric-btn">+ Add</Button>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea name="description" value={productForm.description} onChange={handleFormChange} placeholder="Optional notes about this design..." data-testid="product-description-input" />
          </div>

          <div className="space-y-1.5">
            <Label>Tentative WSP <span className="text-xs text-gray-400 font-normal">(optional)</span></Label>
            <Input type="number" min="0" step="0.01" name="tentative_wsp" value={productForm.tentative_wsp ?? ''}
              onChange={handleFormChange} placeholder="e.g., 750" onWheel={e => e.target.blur()}
              className="max-w-[200px]" data-testid="product-tentative-wsp-input" />
            <p className="text-[11px] text-gray-400">Used as the default rate in Sales Orders for this design until the final WSP is set from a Purchase Entry.</p>
            <div className="pt-2">
              <TentativeWspGroups sizes={enums.sizes || []} groups={productForm.tentative_wsp_groups || []}
                onChange={(g) => setProductForm(p => ({ ...p, tentative_wsp_groups: g }))} />
            </div>
          </div>

          <div className="flex gap-4 pt-6">
            <Button type="submit" className="flex-1" data-testid="submit-product-button">Add New Design</Button>
            <Button type="button" variant="outline" onClick={() => setIsOpen(false)}>Cancel</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ProductFormDialog;
